import React, { useMemo } from 'react';
import { Layer, Line, Circle, Group } from 'react-konva';
import { CELL_SIZE } from './GridLayer';
import { PATH_STYLES } from '../../lib/paths';

// Decorative paths drawn by the DM. Render between TerrainLayer and WallLayer
// in MapCanvas so they sit above floor tiles, below walls/doors, and inherit
// fog occlusion from FogLayer above. No gameplay effect — purely aesthetic.

export default function PathLayer({
  paths        = [],
  draftPts     = [],
  mousePos     = null,
  activeTool   = null,
  activeStyle  = 'dirt',
  selectedPathIds = null,
  onPathMouseDown = null,
  onPathClick     = null,
  dragOffset      = null,
  onSegmentClick  = null,
  draftCurved     = false,
  bare            = false,
}) {
  const isPathPen     = activeTool === 'path-pen';
  const isPathSelect  = activeTool === 'path-select';
  const isPathEraser  = activeTool === 'path-eraser';

  // ── Saved paths ───────────────────────────────────────────────────────────
  const pathElements = useMemo(() => paths.map((path) => {
    const sty = PATH_STYLES[path.style] || PATH_STYLES.dirt;
    if (!path.pts || path.pts.length < 2) return null;
    const sel = selectedPathIds && selectedPathIds.has(path.id);
    const dx = sel && dragOffset ? dragOffset.x * CELL_SIZE : 0;
    const dy = sel && dragOffset ? dragOffset.y * CELL_SIZE : 0;
    const flat = path.pts.flatMap((p) => [p.x * CELL_SIZE + dx, p.y * CELL_SIZE + dy]);
    return (
      <Line
        key={path.id}
        points={flat}
        tension={path.curved ? 0.4 : 0}
        stroke={sel ? '#f5c842' : sty.stroke}
        strokeWidth={sel ? sty.strokeWidth + 3 : sty.strokeWidth}
        lineCap="round"
        lineJoin="round"
        opacity={sel ? 1 : (sty.opacity ?? 1)}
        dash={sty.dash}
        shadowColor={sel ? 'rgba(245,200,66,0.6)' : sty.shadow}
        shadowBlur={sel ? 10 : (sty.shadowBlur ?? 0)}
        hitStrokeWidth={24}
        onMouseDown={(e) => onPathMouseDown?.(path.id, !!e.evt?.shiftKey, e)}
        onClick={(e) => onPathClick?.(path.id, !!e.evt?.shiftKey)}
        listening={isPathSelect}
      />
    );
  }), [paths, selectedPathIds, dragOffset, isPathSelect, onPathClick, onPathMouseDown]);

  // ── Eraser: each segment a separate clickable hit-line ───────────────────
  const eraserElements = useMemo(() => {
    if (!isPathEraser) return null;
    const hoverColor = 'rgba(245,80,80,0.5)';
    const elems = [];
    for (const path of paths) {
      const sty = PATH_STYLES[path.style] || PATH_STYLES.dirt;
      if (!path.pts || path.pts.length < 2) continue;
      for (let i = 0; i < path.pts.length - 1; i++) {
        const a = path.pts[i];
        const b = path.pts[i + 1];
        elems.push(
          <Line
            key={`${path.id}-seg${i}`}
            points={[a.x * CELL_SIZE, a.y * CELL_SIZE, b.x * CELL_SIZE, b.y * CELL_SIZE]}
            stroke="transparent"
            strokeWidth={sty.strokeWidth + 6}
            hitStrokeWidth={28}
            lineCap="round"
            onMouseEnter={(e) => { e.target.stroke(hoverColor); e.target.getLayer().batchDraw(); }}
            onMouseLeave={(e) => { e.target.stroke('transparent'); e.target.getLayer().batchDraw(); }}
            onClick={() => onSegmentClick?.(path.id, i)}
            listening={true}
          />
        );
      }
    }
    return elems;
  }, [isPathEraser, paths, onSegmentClick]);

  // ── Draft preview ────────────────────────────────────────────────────────
  const draftStyle  = PATH_STYLES[activeStyle] || PATH_STYLES.dirt;
  const previewPts  = mousePos ? [...draftPts, mousePos] : draftPts;
  const flatPreview = previewPts.length >= 2
    ? previewPts.flatMap((p) => [p.x * CELL_SIZE, p.y * CELL_SIZE])
    : null;

  const body = (
    <>
      {pathElements}
      {eraserElements}

      {isPathPen && (
        <Group listening={false}>
          {flatPreview && (
            <Line
              points={flatPreview}
              tension={draftCurved ? 0.4 : 0}
              stroke={draftStyle.stroke}
              strokeWidth={draftStyle.strokeWidth}
              lineCap="round"
              lineJoin="round"
              opacity={0.6}
              dash={[10, 6]}
            />
          )}

          {draftPts.map((pt, i) => (
            <Circle
              key={i}
              x={pt.x * CELL_SIZE}
              y={pt.y * CELL_SIZE}
              radius={i === 0 ? 6 : 3.5}
              fill={draftStyle.stroke}
              stroke={i === 0 ? '#ffffff' : 'transparent'}
              strokeWidth={1.5}
              opacity={0.9}
            />
          ))}

          {mousePos && (
            <Circle
              x={mousePos.x * CELL_SIZE}
              y={mousePos.y * CELL_SIZE}
              radius={5}
              fill="#ffffff"
              opacity={0.75}
            />
          )}
        </Group>
      )}
    </>
  );

  if (bare) return body;
  return <Layer listening={isPathSelect || isPathEraser}>{body}</Layer>;
}
